<template src="./user-info.html">
</template>
<script>
  import Vue from 'vue'
  import Loader from '@/components/misc/Loader'
  import { hasPermissionChangeAuthority, hasUserResetPasswordClaim } from '@/router/auth'
  import { EventBus } from '@/router/bus'
  import Vuelidate from 'vuelidate'
  import { required } from 'vuelidate/lib/validators'
  import { validationMixin } from '@/mixins/validation'

  Vue.use(Vuelidate)

  export default {
    name: 'user-info',
    mixins: [validationMixin],
    components: {
      Loader
    },
    data () {
      return {
        priorityVariant: ['success', 'secondary', 'danger', 'danger'],
        UserStatus: {
          ACTIVE: 1,
          INACTIVE: 2,
          REVOKED: 3,
          DISABLED: 4
        },
        roleFields: [
          {
            key: 'selected_role',
            label: ' ',
            sortable: false
          },
          {
            key: 'localized_name',
            label: this.$t('userAdministration.userRoles'),
            sortable: false
          },
          {
            key: 'description',
            label: this.$t('userAdministration.userRoleDescription'),
            sortable: false
          },
          {
            key: 'details',
            label: ' ',
            sortable: false
          }
        ],
        roleItems: [],
        user: {},
        selectedRoles: [],
        selectedClaims: [],
        load: false,
        canChangeAuthority: false,
        editModeActive: false,
        filter: null,
        organizationLink: '',
        userChangedLink: '',
        hasUserResetPasswordClaim: hasUserResetPasswordClaim()
      }
    },
    props: {
      userInformationUrl: {
        type: String
      },
      revokeUserUrl: {
        type: String
      },
      restoreUserUrl: {
        type: String
      },
      updateUserPermissionsUrl: {
        type: String
      },
      globalUser: {
        type: Boolean,
        default: false
      }
    },
    watch: {
      '$route.params.userId' () {
        this.retrieveUserInfo(this.$route.params.userId)
      }
    },
    computed: {
      userRevoked () {
        return this.user.status === this.UserStatus.REVOKED || this.user.status === this.UserStatus.DISABLED
      },
      userRevokedLink () {
        return 'user-administration/local-user/' + this.user.changedId
      },
      indeterminate () {
        let retVal = []
        this.roleItems.forEach((item) => {
          let itemObject = {}
          if (this.containsSomeClaims(item.claims, this.selectedClaims)) {
            itemObject[item.name] = true
            retVal.push(itemObject)
          } else {
            itemObject[item.name] = false
            retVal.push(itemObject)
          }
        })
        return retVal
      }
    },
    created: function () {
      this.retrieveUserInfo(this.$route.params.userId)
    },
    methods: {
      retrieveUserInfo (userId) {
        this.$http.get(this.userInformationUrl + '/' + userId)
          .then(response => {
            this.logConsole(response)
            this.user = response.data.payload

            this.canChangeAuthority = hasPermissionChangeAuthority(this.user.creationClaims)
            this.organizationLink = '/user-registration/approval/' + this.user.organizationId
            this.userChangedLink = '/user-administration/local-user/' + this.user.changedId
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
          })
      },
      revokeUser (evt) {
        this.logConsole('Revoking user ' + this.user)
        this.load = true
        let request = []
        request.push(this.user)

        this.$http.post(this.revokeUserUrl, request)
          .then(response => {
            this.logConsole(response)
            this.load = false
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('userAdministration.userRevoked'),
              errors: []
            })
            this.user.status = this.UserStatus.REVOKED
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      restoreUser (evt) {
        this.logConsole('Restoring user ' + this.user)
        this.load = true
        let request = []
        request.push(this.user)

        this.$http.post(this.restoreUserUrl, request)
          .then(response => {
            this.logConsole(response)
            this.load = false
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('userAdministration.userRestored'),
              errors: []
            })
            this.user.status = this.UserStatus.ACTIVE
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      editRoles () {
        this.load = true

        this.$http.get(Vue.$apiConfig.userPermissionHierarchyGet)
          .then(response => {
            this.logConsole(response)
            let permissionHierarchy = response.data.payload
            this.roleItems = this.getAvailableRoles(permissionHierarchy)
            this.selectRoles()
            this.load = false
            this.editModeActive = true
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      selectRoles () {
        if (Array.isArray(this.user.creationRoles)) {
          for (let i = 0; i < this.user.creationRoles.length; i++) {
            this.addRole(this.user.creationRoles[i])
          }
        } else {
          this.addRole(this.user.creationRoles)
        }

        if (Array.isArray(this.user.creationClaims)) {
          for (let j = 0; j < this.user.creationClaims.length; j++) {
            this.addClaim(this.user.creationClaims[j])
          }
        } else {
          this.addClaim(this.user.creationClaims)
        }

        for (let i = 0; i < this.roleItems.length; i++) {
          // Select role if user has all containing claims
          if (this.containsAllClaims(this.roleItems[i].claims, this.user.creationClaims)) {
            this.addRole(this.roleItems[i].name)
          }
        }
      },
      getAvailableRoles (permissionHierarchy) {
        let roles = []
        let availableRoles = []
        if (Array.isArray(permissionHierarchy)) {
          // ES6 distinct
          availableRoles = [...new Set(permissionHierarchy.map(item => item.roleDescription))]
          availableRoles.forEach((availableRole) => {
            let isNull = !availableRole
            roles.push({
              name: isNull ? this.$t('userAdministration.ungroupedClaims') : availableRole,
              localized_name: isNull ? this.$t('userAdministration.ungroupedClaims') : this.$t('userAdministration.' + availableRole),
              description: isNull ? this.$t('userAdministration.Ungrouped_Description') : this.$t('userAdministration.' + availableRole + '_Description'),
              valid_role: !isNull,
              claims: []
            })
          })
          permissionHierarchy.forEach((item) => {
            let roleSearchCriteria = item.roleDescription === null ? this.$t('userAdministration.ungroupedClaims') : item.roleDescription
            var result = roles.filter((obj) => obj.name.localeCompare(roleSearchCriteria) === 0)
            if (result && result[0]) {
              result[0].claims.push({
                text: this.$t('userAdministration.' + item.claimDescription),
                value: item.claimDescription,
                id: item.claimId
              })
            }
          })
        }
        return roles
      },
      cancelEditRoles () {
        this.editModeActive = false
      },
      saveEditRoles () {
        this.load = true
        let ungroupedIndex = this.selectedRoles.indexOf(this.$t('userAdministration.ungroupedClaims'))
        if (ungroupedIndex >= 0) { this.selectedRoles.splice(ungroupedIndex, 1) }

        this.user.creationRoles = this.selectedRoles
        this.user.creationClaims = this.selectedClaims
        this.$http.post(this.updateUserPermissionsUrl, this.user)
          .then(response => {
            this.logConsole(response)
            this.load = false
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('userAdministration.userPermissionsUpdated'),
              errors: []
            })

            this.editModeActive = false
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      roleSelected (role) {
        let index = this.selectedRoles.indexOf(role.name)
        if (index < 0) {
          this.selectedRoles.push(role.name)
        } else {
          this.selectedRoles.splice(index, 1)
        }

        for (let j = 0; j < role.claims.length; j++) {
          let claimIndex = this.selectedClaims.indexOf(role.claims[j].value)
          if (claimIndex >= 0 && index >= 0) {
            this.selectedClaims.splice(claimIndex, 1)
          } else if (claimIndex < 0 && index < 0) {
            this.addClaim(role.claims[j].value)
          }
          this.claimChanged(role.claims[j].value)
        }
      },
      claimSelected (claim) {
        let index = this.selectedClaims.indexOf(claim.value)
        if (index < 0) {
          this.selectedClaims.push(claim.value)
        } else {
          this.selectedClaims.splice(index, 1)
        }

        this.claimChanged(claim)
      },
      claimChanged (claim) {
        for (let i = 0; i < this.roleItems.length; i++) {
          if (this.containsAllClaims(this.roleItems[i].claims, this.selectedClaims)) {
            this.addRole(this.roleItems[i].name)
          } else {
            this.removeRole(this.roleItems[i].name)
          }
        }
      },
      containsAllClaims (role, claims) {
        let containsAllClaims = true

        for (let i = 0; i < role.length; i++) {
          if (Array.isArray(claims)) {
            if (claims.indexOf(role[i].value) >= 0) {
              containsAllClaims &= true
            } else {
              return false
            }
          } else {
            if (claims.localeCompare(role[i].value) === 0) {
              containsAllClaims &= true
            } else {
              return false
            }
          }
        }
        return containsAllClaims
      },
      // Check's to see if role contains some claims but not all
      containsSomeClaims (role, claims) {
        let numberOfClaims = 0
        for (let i = 0; i < role.length; i++) {
          if (Array.isArray(claims)) {
            if (claims.indexOf(role[i].value) >= 0) {
              numberOfClaims += 1
            }
          } else {
            if (claims.localeCompare(role[i].value) === 0) {
              numberOfClaims += 1
            }
          }
        }
        return numberOfClaims > 0 && numberOfClaims < role.length
      },
      roleModelChanged (role) {
        let index = this.selectedRoles.indexOf(role)
        if (index < 0) {
          this.selectedRoles.push(role)
        } else {
          this.selectedRoles.splice(index, 1)
        }
      },
      addRole (role) {
        if (this.selectedRoles.indexOf(role) < 0) {
          this.selectedRoles.push(role)
        }
      },
      removeRole (role) {
        let index = this.selectedRoles.indexOf(role)
        if (this.selectedRoles.indexOf(role) >= 0) {
          this.selectedRoles.splice(index, 1)
        }
      },
      claimModelChanged (claim) {
        let index = this.selectedClaims.indexOf(claim)
        if (index < 0) {
          this.selectedClaims.push(claim)
        } else {
          this.selectedClaims.splice(index, 1)
        }
      },
      addClaim (claim) {
        if (this.selectedClaims.indexOf(claim) < 0) {
          this.selectedClaims.push(claim)
        }
      },
      validateUserInformation () {
        this.logConsole(this.user)
        this.$v.$touch()
        let userValidationErrors = this.getValidationErrors('user')
        if (userValidationErrors && userValidationErrors.length > 0) {
          EventBus.$emit('alert', {
            variant: 'danger',
            message: this.$t('errors.someErrorsOccuredCheckListBelow'),
            errors: this.getValidationErrors('user')
          })
          return
        }
        this.editUser()
      },
      updateChangedPermissions () {
        this.$refs.permissionUpdateConfirmationModal.show()
      },
      editUser () {
        this.load = true
        this.$http.post(Vue.$apiConfig.updateLocalUserInformationPost, this.user)
          .then(response => {
            this.logConsole(response)
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('userAdministration.userUpdated'),
              errors: []
            })
            this.load = false
            this.$v.$reset()
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      },
      resetUserPassword () {
        this.load = true
        this.$http.patch(
          Vue.$apiConfig.resetLocalUserPasswordPatch,
          [{ userId: this.user.userId }]
        )
          .then(response => {
            this.logConsole(response)
            EventBus.$emit('alert', {
              variant: 'success',
              message: this.$t('userAdministration.userPasswordHasBeenReset'),
              errors: []
            })
            this.load = false
            this.$v.$reset()
          }, error => {
            this.logConsole(error)
            this.formatErrorResponse(error)
            this.load = false
          })
      }
    },
    validations: {
      user: {
        login: {
          required
        },
        name: {
          required
        },
        email: {
          required
        }
      }
    }
  }
</script>

<style lang="scss" type="text/scss" scoped>

  @import '~@/assets/scss/variables.scss';

  .user-info-label {
    font-weight: 600;
  }

  .bg-primary {
    background-color: $primary-color-accent !important;
  }
  
  .text-dark {
    font-weight: 600;
    font-size: 12pt;
    color: $font-color-dark !important;
  }
  
  .user-revoked-card {
    background-color: transparentize($background-color-danger, .9) !important;
  }

  .contents-highlight {
    font-weight: 600;
    font-size: 11pt;
    cursor: pointer;
    &:hover {
      color: $primary-color-accent;
    }
  }

</style>



// WEBPACK FOOTER //
// UserInfo.vue?5847f544